import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  Card,
  CardContent,
  Alert,
  Chip,
} from '@mui/material';
import {
  Message as MessageIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/api';

const MessageTemplates = () => {
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState({
    invoice_reminder_sms: '',
    invoice_reminder_email_subject: '',
    invoice_reminder_email_body: '',
    billing_reminder_sms: '',
    deposit_reminder_sms: '',
    payment_confirmation_sms: '',
    payment_confirmation_email_subject: '',
    payment_confirmation_email_body: '',
  });

  const defaultTemplates = {
    invoice_reminder_sms: 'Dear {tenant_name}, your rent invoice {invoice_number} for KES {amount} is due on {due_date}. Pay via M-Pesa Paybill {paybill}, Acc: {invoice_number}. Avoid late fees. - {from_name}',
    invoice_reminder_email_subject: 'Rent Invoice Reminder - {invoice_number}',
    invoice_reminder_email_body: 'Dear {tenant_name},\n\nThis is a reminder that your rent invoice {invoice_number} for KES {amount} is due on {due_date}.\n\nHow to Pay:\nM-Pesa Paybill: {paybill}\nAccount Number: {invoice_number}\n\nPlease make payment to avoid late fees.\n\nThank you,\n{from_name}',
    billing_reminder_sms: 'Dear {tenant_name}, your utility bill {invoice_number} for KES {amount} is due on {due_date}. Pay via M-Pesa Paybill {paybill}, Acc: {invoice_number}. - {from_name}',
    deposit_reminder_sms: 'Dear {tenant_name}, your security deposit invoice {invoice_number} for KES {amount} is due on {due_date}. Pay via M-Pesa Paybill {paybill}, Acc: {invoice_number}. - {from_name}',
    payment_confirmation_sms: 'Dear {tenant_name}, we received your payment of KES {amount_paid} for invoice {invoice_number}. Thank you! - {from_name}',
    payment_confirmation_email_subject: 'Payment Confirmation - {invoice_number}',
    payment_confirmation_email_body: 'Dear {tenant_name},\n\nWe have received your payment of KES {amount_paid} for invoice {invoice_number}.\n\nThank you for your payment!\n\nBest regards,\n{from_name}',
  };

  const availableVariables = [
    { key: '{tenant_name}', description: 'Tenant\'s full name' },
    { key: '{invoice_number}', description: 'Invoice number' },
    { key: '{amount}', description: 'Invoice amount' },
    { key: '{amount_paid}', description: 'Amount paid' },
    { key: '{due_date}', description: 'Invoice due date' },
    { key: '{from_name}', description: 'Your organization name' },
    { key: '{paybill}', description: 'M-Pesa Paybill / Till number (set in System Config)' },
  ];

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/api/config/message-templates');
      if (response.data) {
        setTemplates(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch message templates:', error);
      // If templates don't exist, use defaults
      setTemplates(defaultTemplates);
    }
  };

  const handleChange = (e) => {
    setTemplates({
      ...templates,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/api/config/message-templates', templates);
      toast.success('Message templates saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save message templates');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Reset all templates to default values?')) {
      setTemplates(defaultTemplates);
      toast.info('Templates reset to defaults. Click Save to apply changes.');
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <MessageIcon color="primary" />
        <Typography variant="h6">Message Templates</Typography>
      </Box>
      <Divider sx={{ mb: 3 }} />

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" gutterBottom>
          Customize the SMS/email messages sent to tenants for rent, billing, deposit reminders and payment confirmations.
        </Typography>
        <Typography variant="body2">
          Use the variables below to personalize your messages. They will be automatically replaced with actual values.
          Set your <strong>M-Pesa Paybill</strong> number in <em>System Config → Invoice &amp; Payment Settings</em> to enable the <code>{'{paybill}'}</code> variable. The account reference is always the <strong>invoice number</strong> ({'{invoice_number}'}) so payments are automatically matched to the correct invoice.
        </Typography>
      </Alert>

      {/* Available Variables */}
      <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            Available Variables:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            {availableVariables.map((variable) => (
              <Chip
                key={variable.key}
                label={`${variable.key} - ${variable.description}`}
                size="small"
                variant="outlined"
              />
            ))}
          </Box>
        </CardContent>
      </Card>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Rent Invoice Reminder Templates */}
          <Grid item xs={12}>
            <Typography variant="h6" color="primary" gutterBottom>
              Rent Invoice Reminder Templates
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Rent Reminder SMS"
              name="invoice_reminder_sms"
              value={templates.invoice_reminder_sms}
              onChange={handleChange}
              helperText={`${templates.invoice_reminder_sms.length} chars — include {paybill} and {account} for payment instructions`}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Invoice Reminder Email Subject"
              name="invoice_reminder_email_subject"
              value={templates.invoice_reminder_email_subject}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={6}
              label="Invoice Reminder Email Body"
              name="invoice_reminder_email_body"
              value={templates.invoice_reminder_email_body}
              onChange={handleChange}
              helperText="Use \n for line breaks"
            />
          </Grid>

          {/* Billing & Utility Reminder SMS */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" color="primary" gutterBottom>
              Billing / Utility Reminder SMS
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Billing Reminder SMS (Water, Electricity, etc.)"
              name="billing_reminder_sms"
              value={templates.billing_reminder_sms}
              onChange={handleChange}
              helperText={`${(templates.billing_reminder_sms || '').length} chars — sent when a utility bill invoice is generated`}
            />
          </Grid>

          {/* Deposit Reminder SMS */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" color="primary" gutterBottom>
              Security Deposit Reminder SMS
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Deposit Invoice SMS"
              name="deposit_reminder_sms"
              value={templates.deposit_reminder_sms}
              onChange={handleChange}
              helperText={`${(templates.deposit_reminder_sms || '').length} chars — sent when a security deposit invoice is generated`}
            />
          </Grid>

          {/* Payment Confirmation Templates */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" color="primary" gutterBottom>
              Payment Confirmation Templates
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Payment Confirmation SMS"
              name="payment_confirmation_sms"
              value={templates.payment_confirmation_sms}
              onChange={handleChange}
              helperText="Keep it short (160 characters recommended for SMS)"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Payment Confirmation Email Subject"
              name="payment_confirmation_email_subject"
              value={templates.payment_confirmation_email_subject}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={6}
              label="Payment Confirmation Email Body"
              name="payment_confirmation_email_body"
              value={templates.payment_confirmation_email_body}
              onChange={handleChange}
              helperText="Use \n for line breaks"
            />
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box display="flex" gap={2}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                Save Templates
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleReset}
                disabled={loading}
              >
                Reset to Defaults
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default MessageTemplates;
